import React, { useState, useEffect } from "react";
import { firebaseAuth, db } from "../../../context/Firebase";
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  CircularProgress,
  Box,
  Typography,
  Fab,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Divider,
  Badge,
  IconButton,
  InputAdornment,
  Chip,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add,
  Search,
  Group,
  Settings,
  Notifications,
} from "@mui/icons-material";
import ImprovedChat from "./ImprovedChat";
import Sidebar from "../../../shared/components/Sidebar";

const ImprovedUserList = () => {
  const [authUser, authLoading] = useAuthState(firebaseAuth);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userNames, setUserNames] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [conversationSearch, setConversationSearch] = useState("");

  useEffect(() => {
    if (authUser) {
      const unsubscribe = loadConversations();
      return () => {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const fetchUserName = async (userId) => {
    if (userNames[userId]) return userNames[userId];

    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const name = userDoc.data().name || userDoc.data().email || "User";
        setUserNames((prev) => ({ ...prev, [userId]: name }));
        return name;
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
    return "User";
  };

  const loadConversations = () => {
    try {
      setLoading(true);

      const q = query(
        collection(db, "chats"),
        where("participants", "array-contains", authUser.uid)
      );

      const unsubscribe = onSnapshot(q, async (snapshot) => {
        const chats = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        for (const chat of chats) {
          const otherUserId = chat.participants.find((p) => p !== authUser.uid);
          if (otherUserId) {
            await fetchUserName(otherUserId);
          }
        }

        chats.sort((a, b) => {
          const timeA = a.lastMessageTimestamp
            ? new Date(a.lastMessageTimestamp).getTime()
            : 0;
          const timeB = b.lastMessageTimestamp
            ? new Date(b.lastMessageTimestamp).getTime()
            : 0;
          return timeB - timeA;
        });

        setConversations(chats);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error loading conversations:", error);
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const users = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((user) => user.id !== authUser.uid);
      setAllUsers(users);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleNewChat = async () => {
    await loadAllUsers();
    setShowNewChatDialog(true);
  };

  const handleSelectUser = (user) => {
    setSelectedChat({
      userId: user.id,
      userName: user.name || user.email,
    });
    setShowNewChatDialog(false);
  };

  const handleSelectConversation = (conv) => {
    const otherUserId = conv.participants.find((p) => p !== authUser.uid);
    const otherUserName = userNames[otherUserId] || "User";
    setSelectedChat({
      userId: otherUserId,
      userName: otherUserName,
      conversationId: conv.id,
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } else if (diff < 48 * 60 * 60 * 1000) {
      return "Yesterday";
    } else if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const filteredUsers = allUsers.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredConversations = conversations.filter((conv) => {
    const otherUserId = conv.participants.find((p) => p !== authUser.uid);
    const otherUserName = userNames[otherUserId] || "";
    
    // Apply search filter
    const matchesSearch = otherUserName
      .toLowerCase()
      .includes(conversationSearch.toLowerCase());
    
    // Apply tab filter
    if (tabValue === 0) {
      // All - show all conversations
      return matchesSearch;
    } else if (tabValue === 1) {
      // Unread - show only conversations with unread messages
      return matchesSearch && (conv.unreadCount || 0) > 0;
    } else if (tabValue === 2) {
      // Groups - show only group conversations
      return matchesSearch && conv.type === 'group';
    }
    
    return matchesSearch;
  });

  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!authUser) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography>Please login to view chats</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", display: "flex" }}>
      <Sidebar />
      <Box sx={{ flex: 1, display: "flex", bgcolor: "var(--bg-primary)" }}>
        {/* Left Side - Chat List */}
        <Box
          sx={{
            width: 420,
            display: "flex",
            flexDirection: "column",
            bgcolor: "var(--card-bg)",
            borderRight: "1px solid var(--border-color)",
          }}
        >
          {/* Header */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              bgcolor: "var(--app-accent)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" fontWeight={700}>
              Chats
            </Typography>
            <Box>
              <IconButton sx={{ color: "white" }}>
                <Group />
              </IconButton>
              <IconButton sx={{ color: "white" }}>
                <Notifications />
              </IconButton>
              <IconButton sx={{ color: "white" }}>
                <Settings />
              </IconButton>
            </Box>
          </Paper>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="All" />
            <Tab label="Unread" />
            <Tab label="Groups" />
          </Tabs>

          {/* Search */}
          <Box sx={{ p: 2, bgcolor: "var(--bg-secondary)" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={conversationSearch}
              onChange={(e) => setConversationSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "var(--input-bg)",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>

          {/* Conversations List */}
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {filteredConversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color="text.secondary" gutterBottom>
                    No chats yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click + to start a new chat
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {filteredConversations.map((conv) => {
                    const otherUserId = conv.participants.find(
                      (p) => p !== authUser.uid
                    );
                    const otherUserName = userNames[otherUserId] || "Loading...";
                    const isSelected =
                      selectedChat?.conversationId === conv.id ||
                      selectedChat?.userId === otherUserId;

                    let lastMessageText = "";
                    if (typeof conv.lastMessage === "string") {
                      lastMessageText = conv.lastMessage;
                    } else if (
                      conv.lastMessage &&
                      typeof conv.lastMessage === "object"
                    ) {
                      lastMessageText = conv.lastMessage.text || "";
                    }

                    const unreadCount = conv.unreadCount || 0;

                    return (
                      <React.Fragment key={conv.id}>
                        <ListItem
                          button
                          onClick={() => handleSelectConversation(conv)}
                          sx={{
                            bgcolor: isSelected ? "var(--hover-bg)" : "var(--card-bg)",
                            "&:hover": { bgcolor: "var(--hover-bg)" },
                            py: 2,
                            px: 2,
                          }}
                        >
                          <ListItemAvatar>
                            <Badge
                              color="success"
                              variant="dot"
                              overlap="circular"
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "var(--app-accent)",
                                  width: 50,
                                  height: 50,
                                }}
                              >
                                {otherUserName[0]?.toUpperCase()}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography fontWeight={600} variant="body1">
                                  {otherUserName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatTime(conv.lastMessageTimestamp)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mt={0.5}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ maxWidth: "70%" }}
                                >
                                  {lastMessageText || "No messages yet"}
                                </Typography>
                                {unreadCount > 0 && (
                                  <Chip
                                    label={unreadCount}
                                    size="small"
                                    color="primary"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          )}

          {/* New Chat FAB */}
          <Fab
            color="primary"
            aria-label="new chat"
            onClick={handleNewChat}
            sx={{
              position: "absolute",
              bottom: 24,
              left: 340,
              bgcolor: "var(--app-accent)",
              "&:hover": { bgcolor: "var(--app-accent-hover)" },
            }}
          >
            <Add />
          </Fab>
        </Box>

        {/* Right Side - Chat Window */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {selectedChat ? (
            <ImprovedChat
              selectedUserId={selectedChat.userId}
              selectedUserName={selectedChat.userName}
              selectedConversationId={selectedChat.conversationId}
              onBack={() => setSelectedChat(null)}
            />
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
              bgcolor="var(--bg-primary)"
              sx={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d9d9d9' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            >
              <Box textAlign="center" sx={{ maxWidth: 500, p: 4 }}>
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    margin: "0 auto 24px",
                    bgcolor: "var(--card-bg)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 3,
                  }}
                >
                  <Typography variant="h1" sx={{ fontSize: 80 }}>
                    💬
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  color="text.primary"
                  gutterBottom
                  fontWeight={600}
                >
                  Welcome to Chat
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Select a conversation to start messaging or click the + button
                  to start a new chat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send and receive messages, photos, and more
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* New Chat Dialog */}
        <Dialog
          open={showNewChatDialog}
          onClose={() => setShowNewChatDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight={600}>
              New Chat
            </Typography>
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <List>
              {filteredUsers.length === 0 ? (
                <Typography color="text.secondary" align="center">
                  No users found
                </Typography>
              ) : (
                filteredUsers.map((user) => (
                  <ListItem
                    key={user.id}
                    button
                    onClick={() => handleSelectUser(user)}
                    sx={{
                      "&:hover": { bgcolor: "var(--hover-bg)" },
                      borderRadius: 2,
                      mb: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={user.picture} sx={{ bgcolor: "var(--app-accent)" }}>
                        {user.name?.[0]?.toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography fontWeight={600}>
                          {user.name || "Unknown"}
                        </Typography>
                      }
                      secondary={user.email}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ImprovedUserList;
